import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { TBrowserApiData } from '@/core/constants/event_types/additional_types';
import { TPlatformIdentifiers } from '@/core/constants/event_types/base_mable_event';

import { GenericDataHandler } from '../generic_data_handler';
import { StorageHandler } from '../storage_handler';

const defaultPlatformIdentifiers: TPlatformIdentifiers = {
  epik: [],
  fbclid: [],
  gacid: [],
  gclid: [],
  ttclid: [],
};
const addUnique = (array: string[], value: string) => {
  if (value && !array.includes(value)) array.push(value);
};

export class PlatformIdentifiersHandler {
  private static readonly _storageHandler =
    new StorageHandler<TPlatformIdentifiers>(
      'platformIdentifiers',
      defaultPlatformIdentifiers,
      EBrowserStorageType.LOCALSTORAGE,
    );
  private static readonly genericDataHandler =
    new GenericDataHandler<TPlatformIdentifiers>();

  static get(): TPlatformIdentifiers {
    return this._storageHandler.get();
  }

  static initOrLoadAndMaybeUpdate(browserData: TBrowserApiData): {
    platformIdentifiers: TPlatformIdentifiers;
    platformIdentifiersChanged: boolean;
  } {
    const newIdentifiers = this._parseQueryParams(
      browserData.qpar,
      browserData.cks,
    );
    const currentIdentifiers = this.get();
    const { dataChanged, mergedData } = this.genericDataHandler.mergeData(
      currentIdentifiers,
      newIdentifiers,
    );

    this._storageHandler.set(mergedData);
    return {
      platformIdentifiers: mergedData,
      platformIdentifiersChanged: dataChanged,
    };
  }

  static updateFromIdentity(newIdentifiers: TPlatformIdentifiers): {
    platformIdentifiers: TPlatformIdentifiers;
    platformIdentifiersChanged: boolean;
  } {
    const { dataChanged, mergedData } = this.genericDataHandler.mergeData(
      this.get(),
      newIdentifiers,
    );

    this._storageHandler.set(mergedData);
    return {
      platformIdentifiers: mergedData,
      platformIdentifiersChanged: dataChanged,
    };
  }

  private static _parseQueryParams(
    qpar: Record<string, string[]>,
    cookies: Record<string, string>,
  ): TPlatformIdentifiers {
    const identifiers = { ...this._storageHandler.get() };
    if (!qpar) return identifiers;
    Object.entries(qpar).forEach(([key, value]) => {
      const firstValue = value.flat()[0]; // Always take the first entry in the array
      if ((key === 'fbclid' || key === '_fbclid') && firstValue)
        addUnique(identifiers.fbclid, firstValue);
      if ((key === 'ttclid' || key === '_ttclid') && firstValue)
        addUnique(identifiers.ttclid, firstValue);
      if ((key === 'epik' || key === '_epik') && firstValue)
        addUnique(identifiers.epik, firstValue);
      if ((key === 'gclid' || key === '_gclid') && firstValue)
        addUnique(identifiers.gclid, firstValue);
      // if (key === 'gacid') addUnique(identifiers.gacid, firstValue); not used currently
    });

    // get the _ga cookie value - e.g. "GA1.2.987654321.0123456789" take everything after the second dot 987654321.0123456789
    const gaCookie = cookies['_ga'];
    if (gaCookie) {
      identifiers.gacid = identifiers.gacid || [];
      const gaCid = gaCookie.split('.').slice(2).join('.');
      if (gaCid && gaCid.length > 0) {
        addUnique(identifiers.gacid, gaCookie);
      }
    }

    return identifiers;
  }
}
